/**
 * Notifications API Service (KAN-330)
 *
 * Thin client over the org-scoped /notifications endpoints. Auth + the active
 * X-Organization-Id header are injected centrally by auth.interceptor, so this
 * service never has to think about scoping - the backend filters every row by
 * recipient AND org.
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AppNotification } from '../types/notification.types';
import { PaginationService, PaginatedResponse } from './pagination.service';

@Injectable({ providedIn: 'root' })
export class NotificationsApiService {
  private apiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private paginationService: PaginationService
  ) {}

  /** Paginated list of the current user's notifications (newest first). */
  getNotifications(page = 1, pageSize = 20, unreadOnly = false): Observable<PaginatedResponse<AppNotification>> {
    const params: Record<string, string> = { page: `${page}`, limit: `${pageSize}` };
    if (unreadOnly) params['unreadOnly'] = 'true';
    return this.http
      .get<any>(`${this.apiUrl}/notifications`, { params })
      .pipe(map((r) => this.paginationService.fromEnvelope<AppNotification>(r.data ?? r)));
  }

  /** Unread count for the bell badge. */
  getUnreadCount(): Observable<number> {
    return this.http
      .get<any>(`${this.apiUrl}/notifications/unread-count`)
      .pipe(map((r) => (r?.data?.count ?? r?.count ?? 0) as number));
  }

  /** Mark a single notification read. */
  markRead(id: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/notifications/${id}/read`, {});
  }

  /** Mark every unread notification read. */
  markAllRead(): Observable<any> {
    return this.http.post(`${this.apiUrl}/notifications/read-all`, {});
  }
}
